import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { 
  LogOut, Menu, X, User, Sun, Moon, BookOpen, ChevronDown, ChevronRight,
  LayoutDashboard, ClipboardList, Heart, Microscope, TrendingUp, Palette, 
  Pin, Hexagon, FolderOpen, Users, Building2, UserCog, 
  Calendar, FileText, TrendingUpIcon, Mail, Clock, Coffee, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import DashboardHome from '../components/DashboardHome';
import DepartmentManagement from '../components/DepartmentManagement';
import UserManagement from '../components/UserManagement';
import TaskManagement from '../components/TaskManagement';
import TaskView from '../components/TaskView';
import HiveBoard from '../components/HiveBoard';
import SOPManagement from '../components/SOPManagement';
import SOPDetail from './SOPDetail';
import DailyScanForm from '../components/DailyScanForm';
import Analytics from '../components/Analytics';
import PasswordResetModal from '../components/PasswordResetModal';
import ProfileModal from '../components/ProfileModal';
import NewsletterManagement from '../components/NewsletterManagement';
import TaskCreate from '../pages/TaskCreate';
import UserProfile from '../pages/UserProfile';
import TaskDetail from '../pages/TaskDetail';
import CourseList from '../components/CourseList';
import CourseManagement from '../components/CourseManagement';
import ProjectManagement from '../components/ProjectManagement';
import ProjectView from '../pages/ProjectView';
import SubProjectView from '../pages/SubProjectView';
import ProgramManagement from '../components/ProgramManagement';
import ProgramDetail from '../components/ProgramDetail';
import SessionViewer from '../components/SessionViewer';
import EducatorDashboard from '../components/EducatorDashboard';
import Leaderboard from '../components/Leaderboard';
import CNESSTasks from '../components/CNESSTasks';
import SelfCare from '../components/SelfCare';
import RnDHub from '../components/RnDHub';
import PersonalGrowth from '../components/PersonalGrowth';
import Creative from '../components/Creative';
import Whiteboard from '../components/Whiteboard';
import Attendance from '../components/Attendance';
import LeaveManagement from '../components/LeaveManagement';
import TeamSettings from '../components/TeamSettings';
import MyAnalytics from '../components/MyAnalytics';
import TeamMembers from '../components/TeamMembers';
import MyLeaveBalance from '../components/MyLeaveBalance';
import '../styles/Dashboard.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]); // Start with all dropdowns closed
  const location = useLocation();

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
    };

    // Initial setup: set mobile state and initial sidebar state
    const mobile = window.innerWidth <= 1024;
    setIsMobile(mobile);
    setSidebarOpen(!mobile); // Open on desktop, closed on mobile

    // Add resize listener (only updates isMobile, doesn't force sidebar state)
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      if (userData.first_login) {
        setShowPasswordReset(true);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    toast.success('Profile updated successfully');
  };

  const getMenuItems = () => {
    if (!user) return [];

    const role = user.role;
    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
      { path: '/dashboard/projects', label: 'Projects', icon: <FolderOpen size={20} /> },
    ];

    // 6 Dimensions - as top-level menu items (Projects moved to baseItems)
    const dimensionItems = [
      { path: '/dashboard/cness-tasks', label: 'Tasks', icon: <ClipboardList size={20} /> },
      { path: '/dashboard/self-care', label: 'Self Care', icon: <Heart size={20} /> },
      { path: '/dashboard/rnd', label: 'R&D', icon: <Microscope size={20} /> },
      { path: '/dashboard/personal-growth', label: 'Personal Growth', icon: <TrendingUp size={20} /> },
      { path: '/dashboard/creative', label: 'Creative', icon: <Palette size={20} /> },
    ];

    // Team Menu - Departments & User Management (Admin/Manager version)
    const teamMenuAdmin = {
      label: 'Team',
      icon: <Users size={20} />,
      isSubmenu: true,
      children: [
        { path: '/dashboard/departments', label: 'Departments', icon: <Building2 size={18} /> },
        { path: '/dashboard/users', label: 'User Management', icon: <UserCog size={18} /> },
        { path: '/dashboard/attendance', label: 'Attendance', icon: <Clock size={18} /> },
        { path: '/dashboard/leave', label: 'Leave Management', icon: <Coffee size={18} /> },
        { path: '/dashboard/settings', label: 'Settings', icon: <Settings size={18} /> },
      ]
    };

    // Team Menu for Team Lead (without Settings)
    const teamMenuLead = {
      label: 'Team',
      icon: <Users size={20} />,
      isSubmenu: true,
      children: [
        { path: '/dashboard/team-members', label: 'Team Members', icon: <Users size={18} /> },
        { path: '/dashboard/attendance', label: 'Attendance', icon: <Clock size={18} /> },
        { path: '/dashboard/leave', label: 'Leave Management', icon: <Coffee size={18} /> },
        { path: '/dashboard/my-leave-balance', label: 'My Leave Balance', icon: <Calendar size={18} /> },
      ]
    };

    // Team Menu for Team Members (limited access)
    const teamMenuMember = {
      label: 'Team',
      icon: <Users size={20} />,
      isSubmenu: true,
      children: [
        { path: '/dashboard/team-members', label: 'Team Members', icon: <Users size={18} /> },
        { path: '/dashboard/attendance', label: 'My Attendance', icon: <Clock size={18} /> },
        { path: '/dashboard/leave', label: 'Leave Management', icon: <Coffee size={18} /> },
        { path: '/dashboard/my-leave-balance', label: 'My Leave Balance', icon: <Calendar size={18} /> },
      ]
    };

    if (role === 'superadmin' || role === 'ceo') {
      return [
        ...baseItems,
        ...dimensionItems,
        { path: '/dashboard/whiteboard', label: 'Whiteboard', icon: <Pin size={20} /> },
        { path: '/dashboard/hive-board', label: 'Hive Board', icon: <Hexagon size={20} /> },
        teamMenuAdmin,
        { path: '/dashboard/daily-scan', label: 'Daily Scan', icon: <Calendar size={20} /> },
        { path: '/dashboard/sops', label: 'SOPs', icon: <FileText size={20} /> },
        { path: '/dashboard/analytics', label: 'Analytics', icon: <TrendingUpIcon size={20} /> },
        { path: '/dashboard/newsletter', label: 'Newsletter', icon: <Mail size={20} /> },
      ];
    }

    if (role === 'educator') {
      return [
        ...baseItems,
        { path: '/dashboard/educator-dashboard', label: 'My Analytics', icon: <TrendingUpIcon size={20} /> },
        { path: '/dashboard/programs', label: 'Wellness & Skill Enablement', icon: <BookOpen size={20} /> },
      ];
    }

    if (role === 'manager') {
      return [
        ...baseItems,
        ...dimensionItems,
        { path: '/dashboard/whiteboard', label: 'Whiteboard', icon: <Pin size={20} /> },
        { path: '/dashboard/hive-board', label: 'Hive Board', icon: <Hexagon size={20} /> },
        teamMenuAdmin,
        { path: '/dashboard/daily-scan', label: 'Daily Scan', icon: <Calendar size={20} /> },
        { path: '/dashboard/sops', label: 'SOPs', icon: <FileText size={20} /> },
        { path: '/dashboard/analytics', label: 'Analytics', icon: <TrendingUpIcon size={20} /> },
      ];
    }

    if (role === 'team_lead') {
      return [
        ...baseItems,
        ...dimensionItems,
        { path: '/dashboard/whiteboard', label: 'Whiteboard', icon: <Pin size={20} /> },
        { path: '/dashboard/hive-board', label: 'Hive Board', icon: <Hexagon size={20} /> },
        teamMenuLead,
        { path: '/dashboard/daily-scan', label: 'Daily Scan', icon: <Calendar size={20} /> },
        { path: '/dashboard/sops', label: 'SOPs', icon: <FileText size={20} /> },
        { path: '/dashboard/my-analytics', label: 'My Analytics', icon: <TrendingUpIcon size={20} /> },
      ];
    }

    if (role === 'team_member') {
      return [
        ...baseItems,
        ...dimensionItems,
        { path: '/dashboard/whiteboard', label: 'Whiteboard', icon: <Pin size={20} /> },
        { path: '/dashboard/hive-board', label: 'Hive Board', icon: <Hexagon size={20} /> },
        teamMenuMember,
        { path: '/dashboard/daily-scan', label: 'Daily Scan', icon: <Calendar size={20} /> },
        { path: '/dashboard/sops', label: 'SOPs', icon: <FileText size={20} /> },
        { path: '/dashboard/my-analytics', label: 'My Analytics', icon: <TrendingUpIcon size={20} /> },
      ];
    }

    return baseItems;
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="dashboard-layout" data-testid="dashboard-layout">
      {/* Mobile Overlay - Only show on mobile/tablet when sidebar is open */}
      {isMobile && sidebarOpen && (
        <div 
          className="mobile-overlay" 
          onClick={() => setSidebarOpen(false)}
          onTouchStart={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : 'closed'}`} data-testid="dashboard-sidebar">
        <div className="sidebar-header">
          <h2 data-testid="sidebar-logo" style={{ color: '#E7EDF9', fontFamily: "'Poppins', sans-serif", fontWeight: '700', fontSize: '22px' }}>CNESS Hive</h2>
          <button 
            className="mobile-close-btn" 
            onClick={() => setSidebarOpen(false)}
            onTouchStart={() => setSidebarOpen(false)}
            data-testid="mobile-close-btn"
            aria-label="Close menu"
            style={{ cursor: 'pointer' }}
          >
            <X size={24} />
          </button>
        </div>

        <div className="sidebar-user" data-testid="sidebar-user-info">
          <div className="user-avatar" data-testid="user-avatar">{user.full_name?.charAt(0)}</div>
          <div className="user-info">
            <div className="user-name" data-testid="user-name">{user.full_name}</div>
            <div className="user-role" data-testid="user-role">{user.role?.replace('_', ' ').toUpperCase()}</div>
          </div>
        </div>

        <nav className="sidebar-nav" data-testid="sidebar-nav">
          {getMenuItems().map((item, index) => {
            if (item.isSubmenu) {
              const isExpanded = expandedMenus.includes(item.label);
              const isChildActive = item.children?.some(child => location.pathname === child.path);
              
              return (
                <div key={index} className="nav-submenu-container">
                  <button
                    className={`nav-item nav-submenu-toggle ${isChildActive ? 'active' : ''}`}
                    onClick={() => {
                      setExpandedMenus(prev => 
                        prev.includes(item.label) 
                          ? [] // Close all when clicking the same one
                          : [item.label] // Only keep this one open, close others
                      );
                    }}
                    data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-chevron" style={{ marginLeft: 'auto' }}>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="nav-submenu" style={{ paddingLeft: '20px' }}>
                      {item.children.map((child, childIndex) => (
                        <button
                          key={childIndex}
                          className={`nav-item nav-subitem ${location.pathname === child.path ? 'active' : ''}`}
                          onClick={() => {
                            navigate(child.path);
                            isMobile && setSidebarOpen(false);
                          }}
                          data-testid={`nav-subitem-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span className="nav-icon" style={{ fontSize: '14px' }}>{child.icon}</span>
                          <span className="nav-label" style={{ fontSize: '13px' }}>{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <button
                key={index}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => {
                  navigate(item.path);
                  isMobile && setSidebarOpen(false);
                }}
                data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main" data-testid="dashboard-main">
        <div className="dashboard-topbar" data-testid="dashboard-topbar">
          <button 
            className="menu-toggle" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            onTouchStart={() => setSidebarOpen(!sidebarOpen)}
            data-testid="menu-toggle"
            style={{ cursor: 'pointer' }}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="page-title" data-testid="page-title">Welcome, {user.full_name}</h1>
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="theme-toggle-btn"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span>{isDarkMode ? 'Light' : 'Dark'}</span>
          </button>
          <button 
            className="profile-btn" 
            onClick={() => setShowProfileModal(true)}
            data-testid="profile-btn"
            title="Edit Profile"
          >
            {user.avatar ? (
              <img 
                src={`data:image/png;base64,${user.avatar}`} 
                alt="Avatar"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <User size={20} />
            )}
            <span>Profile</span>
          </button>
        </div>

        <div className="dashboard-content" data-testid="dashboard-content">
          <Routes>
            <Route index element={<DashboardHome user={user} />} />
            <Route path="departments" element={<DepartmentManagement user={user} />} />
            <Route path="users" element={<UserManagement user={user} />} />
            <Route path="attendance" element={<Attendance user={user} />} />
            <Route path="leave" element={<LeaveManagement user={user} />} />
            <Route path="settings" element={<TeamSettings user={user} />} />
            <Route path="my-analytics" element={<MyAnalytics user={user} />} />
            <Route path="team-members" element={<TeamMembers user={user} />} />
            <Route path="my-leave-balance" element={<MyLeaveBalance user={user} />} />
            <Route path="profile/:userId" element={<UserProfile currentUser={user} />} />
            <Route path="projects" element={<ProjectManagement user={user} />} />
            <Route path="projects/:projectId" element={<ProjectView user={user} />} />
            <Route path="projects/:projectId/subprojects/:subProjectId" element={<SubProjectView user={user} />} />
            <Route path="tasks" element={<TaskManagement user={user} />} />
            <Route path="tasks/new" element={<TaskCreate user={user} />} />
            <Route path="tasks/edit/:taskId" element={<TaskCreate user={user} isEditMode={true} />} />
            <Route path="tasks/:taskId" element={<TaskDetail user={user} />} />
            <Route path="task-view" element={<TaskView user={user} />} />
            <Route path="hive-board" element={<HiveBoard user={user} />} />
            <Route path="courses" element={<CourseList user={user} />} />
            <Route path="my-courses" element={<CourseManagement user={user} />} />
            <Route path="programs" element={<ProgramManagement user={user} />} />
            <Route path="programs/:programId" element={<ProgramDetail user={user} />} />
            <Route path="programs/:programId/sessions/:sessionId" element={<SessionViewer user={user} />} />
            <Route path="educator-dashboard" element={<EducatorDashboard user={user} />} />
            <Route path="leaderboard" element={<Leaderboard user={user} />} />
            <Route path="sops" element={<SOPManagement user={user} />} />
            <Route path="sops/:sopId" element={<SOPDetail user={user} />} />
            <Route path="daily-scan" element={<DailyScanForm user={user} />} />
            <Route path="analytics" element={<Analytics user={user} />} />
            <Route path="newsletter" element={<NewsletterManagement user={user} />} />
            <Route path="whiteboard" element={<Whiteboard user={user} />} />
            {/* 6 Dimensions Routes */}
            <Route path="cness-tasks" element={<CNESSTasks user={user} />} />
            <Route path="self-care" element={<SelfCare user={user} />} />
            <Route path="rnd" element={<RnDHub user={user} />} />
            <Route path="personal-growth" element={<PersonalGrowth user={user} />} />
            <Route path="creative" element={<Creative user={user} />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </div>
      </main>

      {showPasswordReset && (
        <PasswordResetModal
          onClose={() => setShowPasswordReset(false)}
          onSuccess={() => {
            const updatedUser = { ...user, first_login: false };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setShowPasswordReset(false);
          }}
        />
      )}

      {showProfileModal && (
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          onProfileUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default Dashboard;